import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, Film, Inbox, Loader2, ListChecks } from 'lucide-react';
import PublicHeader from '../../components/self-service/PublicHeader';
import { useAuth } from '../../components/self-service/auth/useAuth';
import SignInModal from '../../components/self-service/auth/SignInModal';
import { usePublisherConfig } from '../../data/publisherConfigs';
import { apiClient } from '../../lib/apiClient';
import { formatDate } from '../../components/self-service/shared/dateFormat';
import { useCreativePreviewUrl } from '../../components/self-service/shared/useCreativePreviewUrl';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import Banner from '../../components/self-service/shared/Banner';
import ArchiveLibraryEntryModal from '../../components/self-service/shared/ArchiveLibraryEntryModal';
import {
  priorApprovalBadge,
  type ReusableLibraryItem,
} from '../../components/self-service/shared/useReusableLibrary';

type CreativeStatus = 'active' | 'pending_review' | 'voided' | 'retired' | 'withdrawn' | string;

interface CampaignRef {
  submission_id: string;
  campaign_name: string;
  submission_status: string;
  creative_status: CreativeStatus;
  click_through_url: string | null;
  start_date: string | null;
  end_date: string | null;
  updated_at: string;
}

interface LibraryItem {
  filename: string;
  size_bytes: number | null;
  mime_type: string | null;
  first_seen_at: string;
  preview_storage_path: string;
  campaigns: CampaignRef[];
}

interface ArchivedLibraryItem extends LibraryItem {
  archived_at: string;
  archived_by: string;
}

// Map each creative-status value to a chip label + colorway. The label is the
// user-facing word ("Approved", "In review"), not the schema enum.
const STATUS_STYLE: Record<string, { label: string; className: string }> = {
  active: { label: 'Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending_review: { label: 'In review', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  voided: { label: 'Rejected', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  retired: { label: 'Retired', className: 'bg-slate-50 text-slate-600 border-slate-200' },
  withdrawn: { label: 'Withdrawn', className: 'bg-slate-50 text-slate-600 border-slate-200' },
  // CTV-611: buyer removed the creative before any publisher decision.
  abandoned: { label: 'Removed', className: 'bg-slate-50 text-slate-600 border-slate-200' },
};

function statusBadge(status: string) {
  return (
    STATUS_STYLE[status] ?? { label: status, className: 'bg-slate-50 text-slate-600 border-slate-200' }
  );
}

function formatBytes(bytes: number | null): string {
  if (bytes == null || bytes <= 0) return '—';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

// CTV-463 / ADR-0007: per-station prior-approval state pill. Green pill →
// skips review on next reuse; yellow → still requires review (hard veto from
// a prior rejection here); null → unreviewed, render nothing.
const PriorApprovalPill: React.FC<{ badge: 'approved' | 'rejected' | null }> = ({ badge }) => {
  if (!badge) return null;
  if (badge === 'approved') {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-medium">
        Approved
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-medium">
      Previously rejected here
    </span>
  );
};

interface LibraryCardProps {
  item: LibraryItem;
  /** Current station slug — gates `/creatives/preview-url` and drives the
   *  click-through URLs for campaign references. */
  slug: string;
  /** Station accent — drives the Re-use pill tint so per-row CTAs read as
   *  station-branded instead of slate. */
  accentColor?: string;
  // CTV-526: when set, replaces the Re-use action with an Unarchive action
  // and prepends an "Archived {date}" line to the metadata row.
  archivedAt?: string;
  onReuse?: (item: LibraryItem) => void;
  onArchive?: (item: LibraryItem) => void;
  onUnarchive?: (item: LibraryItem) => void;
}

const LibraryCard: React.FC<LibraryCardProps> = ({
  item,
  slug,
  accentColor,
  archivedAt,
  onReuse,
  onArchive,
  onUnarchive,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  // Fetched once per card and reused for both the thumbnail and the expanded
  // player. Eager so the thumbnail is a real first-frame, not a film icon.
  const { url, posterUrl, loading } = useCreativePreviewUrl(slug, item.preview_storage_path);

  const campaignCount = item.campaigns.length;
  const summary =
    campaignCount === 1 ? 'Used in 1 campaign' : `Used in ${campaignCount} campaigns`;

  return (
    <li className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-colors">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full text-left"
      >
        <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="shrink-0 w-32 h-20 rounded-lg bg-slate-900 overflow-hidden flex items-center justify-center">
            {posterUrl ? (
              // CTV-508: server-generated poster (representative non-black frame).
              // pointer-events-none keeps the row click (expand) intact.
              <img
                src={posterUrl}
                alt={item.filename || 'Creative thumbnail'}
                className="w-full h-full object-cover pointer-events-none"
              />
            ) : url ? (
              // muted + preload="metadata" makes the browser render the first
              // frame without playing audio or downloading the whole file.
              // pointer-events-none keeps the row click (expand) intact.
              <video
                src={url}
                muted
                preload="metadata"
                playsInline
                className="w-full h-full object-cover pointer-events-none"
              />
            ) : (
              <Film className="w-6 h-6 text-white/70" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-[15px] font-semibold tracking-tight text-slate-900 truncate">
                {item.filename}
              </span>
              <span className="text-[11px] text-slate-500 tabular-nums">
                {formatBytes(item.size_bytes)}
              </span>
              <span className="text-[11px] text-slate-400">
                First submitted {formatDate(item.first_seen_at)}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
              <span>{summary}</span>
              {archivedAt && (
                <span className="text-[11px] text-slate-400">
                  Archived {formatDate(archivedAt)}
                </span>
              )}
              <PriorApprovalPill badge={priorApprovalBadge(item as unknown as ReusableLibraryItem)} />
            </div>
          </div>

          <div className="flex items-center gap-2 md:self-center">
            {archivedAt ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnarchive?.(item);
                }}
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border hover:brightness-105 transition-all"
                style={accentColor ? {
                  color: accentColor,
                  borderColor: `${accentColor}40`,
                  backgroundColor: `${accentColor}0f`,
                } : {
                  color: '#475569',
                  borderColor: '#e2e8f0',
                  backgroundColor: '#f8fafc',
                }}
              >
                Unarchive
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReuse?.(item);
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border hover:brightness-105 transition-all"
                  style={accentColor ? {
                    color: accentColor,
                    borderColor: `${accentColor}40`,
                    backgroundColor: `${accentColor}0f`,
                  } : {
                    color: '#475569',
                    borderColor: '#e2e8f0',
                    backgroundColor: '#f8fafc',
                  }}
                >
                  Re-use in new campaign
                </button>
                {onArchive && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchive(item);
                    }}
                    className="text-[11px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded-full transition-colors"
                  >
                    Archive
                  </button>
                )}
              </>
            )}
            <ChevronDown
              className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
          <div className="rounded-2xl overflow-hidden bg-black">
            {loading || !url ? (
              <div className="w-full h-56 flex items-center justify-center text-white/70 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading preview…
              </div>
            ) : (
              <video src={url} controls preload="metadata" className="w-full max-h-72 object-contain" />
            )}
          </div>

          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {item.campaigns.map((c) => {
              const badge = statusBadge(c.creative_status);
              const window =
                c.start_date && c.end_date
                  ? `${formatDate(c.start_date)} – ${formatDate(c.end_date)}`
                  : null;
              return (
                <li key={`${c.submission_id}-${c.updated_at}`}>
                  <Link
                    to={`/${slug}/my-campaigns/${c.submission_id}`}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <span className="flex-1 min-w-0 text-[13px] font-medium text-slate-900 truncate">
                      {c.campaign_name}
                    </span>
                    {window && (
                      <span className="text-[11px] text-slate-500 tabular-nums">{window}</span>
                    )}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium tracking-tight ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </li>
  );
};

const AdvertiserCreativeLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated, selectedAdvertiser } = useAuth();
  const { config: stationConfig } = usePublisherConfig(slug);
  const [signInOpen, setSignInOpen] = React.useState(!isAuthenticated);
  const [items, setItems] = React.useState<LibraryItem[]>([]);
  const [archived, setArchived] = React.useState<ArchivedLibraryItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [modal, setModal] = React.useState<
    | { mode: 'archive' | 'unarchive'; item: LibraryItem }
    | null
  >(null);
  // Bump to force re-fetch after archive/unarchive succeeds.
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    setSignInOpen(!isAuthenticated);
  }, [isAuthenticated]);

  const advertiserId = selectedAdvertiser?.advertiserId ?? null;
  React.useEffect(() => {
    if (!isAuthenticated || !slug || !advertiserId) {
      setItems([]);
      setArchived([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get<{ items: LibraryItem[]; archived?: ArchivedLibraryItem[] }>(
        `/creatives/library/${slug}?advertiserId=${encodeURIComponent(advertiserId)}`,
      )
      .then((res) => {
        if (cancelled) return;
        setItems(res.items ?? []);
        setArchived(res.archived ?? []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load library');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, slug, advertiserId, reloadKey]);

  // Re-use handoff: navigate to this station's wizard with the creative pre-
  // attached, using the same `rerunFrom` route-state contract RerunMenu uses.
  // The page is now station-scoped (preview_storage_path lives under :slug),
  // so the target wizard is unambiguously `/${slug}/start/campaign`.
  const handleReuse = React.useCallback(
    (item: LibraryItem) => {
      if (!slug) return;
      const payload = {
        formData: {},
        creatives: [
          {
            storagePath: item.preview_storage_path,
            filename: item.filename,
            mimeType: item.mime_type ?? 'video/mp4',
            sizeBytes: item.size_bytes ?? 0,
            // Dates and weight intentionally left undefined — the wizard
            // re-anchors to its own start/end window and assigns a default
            // weight when the user picks one Creative from the Library.
          },
        ],
        creativeMeta: {
          storagePath: item.preview_storage_path,
          filename: item.filename,
          mimeType: item.mime_type ?? 'video/mp4',
          sizeBytes: item.size_bytes ?? 0,
        },
      };
      navigate(`/${slug}/start/campaign`, { state: { rerunFrom: payload } });
    },
    [navigate, slug],
  );

  const stationAccent = stationConfig?.accentColor || stationConfig?.primaryColor;
  const stationPrimary = stationConfig?.primaryColor;

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <ListChecks className="w-10 h-10 text-slate-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Sign in to view your library</h1>
          <p className="text-sm text-slate-600 mb-6">
            Enter your email and we'll send a 6-digit code to sign in.
          </p>
          <div className="flex items-center justify-center gap-2">
            <PrimaryButton
              type="button"
              onClick={() => setSignInOpen(true)}
              accentColor={stationAccent}
              primaryColor={stationPrimary}
            >
              Sign in
            </PrimaryButton>
          </div>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader publisher={stationConfig ?? undefined} title="My Library" />

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-5">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">My Library</h1>
          <p className="text-sm text-slate-500 mt-1">
            Commercials you've submitted{stationConfig?.name ? ` to ${stationConfig.name}` : ''}.
          </p>
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Loading your library…
          </div>
        ) : error ? (
          <Banner tone="warn" label="Couldn't load your library">
            {error}
          </Banner>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">No commercials yet</p>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              The library fills up as you submit campaigns.
            </p>
            {slug && (
              <Link
                to={`/${slug}/start`}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-[13px] font-semibold text-white rounded-full transition-all hover:brightness-110 active:scale-[0.98]"
                style={{
                  background: `linear-gradient(180deg, ${stationAccent || '#475569'}, ${stationPrimary || stationAccent || '#1e293b'})`,
                  boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px ${stationPrimary || stationAccent || '#1e293b'}`,
                }}
              >
                Start a campaign
              </Link>
            )}
          </div>
        ) : (
          <>
            <ul className="space-y-3">
              {items.map((item) => (
                <LibraryCard
                  key={`${item.filename}::${item.size_bytes ?? ''}`}
                  item={item}
                  slug={slug ?? ''}
                  accentColor={stationAccent}
                  onReuse={handleReuse}
                  onArchive={(it) => setModal({ mode: 'archive', item: it })}
                />
              ))}
            </ul>

            {archived.length > 0 && (
              <details className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden group">
                <summary className="cursor-pointer list-none px-5 py-3 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-between">
                  <span>Archived ({archived.length})</span>
                  <ChevronDown className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" aria-hidden />
                </summary>
                <ul className="space-y-3 px-3 pb-3">
                  {archived.map((item) => (
                    <LibraryCard
                      key={`archived-${item.filename}::${item.size_bytes ?? ''}`}
                      item={item}
                      slug={slug ?? ''}
                      accentColor={stationAccent}
                      archivedAt={item.archived_at}
                      onUnarchive={(it) =>
                        setModal({ mode: 'unarchive', item: it })
                      }
                    />
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </main>

      {modal && advertiserId && slug && (
        <ArchiveLibraryEntryModal
          slug={slug}
          advertiserId={advertiserId}
          filename={modal.item.filename}
          sizeBytes={modal.item.size_bytes}
          mode={modal.mode}
          accentColor={stationAccent}
          onClose={() => setModal(null)}
          onSuccess={() => {
            setModal(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
};

export default AdvertiserCreativeLibraryPage;
